/* eslint-disable import/no-named-default */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import JsonQuery from 'json-query';
import { Dropdown } from 'react-bootstrap';
import {
    hooks as commonHooks,
    types as commonTypes,
    analytics as commonAnalytics,
    utils as commonUtils,
} from '@sitebuilder/common';

import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import multiDropdownStyle from './MultiDropdown.styles';
import { CURRENTOPTION, FIRSTOPTION, PLACEHOLDER } from '.';

interface DynamicEvent {
    eventName: string;
    customEventName?: string;
    analyticItems: AnalyticsItem[];
}

interface AnalyticsItem {
    name: string;
    value: string;
}

interface DropdownList extends DropdownType {
    isOpen?: boolean;
    listData?: any;
    currentSelectedValue?: any;
    items?: any;
}

interface DropdownType {
    id: string;
    label?: string;
    ariaLabel?: string;
    dataKey?: string;
    itemDataKey?: string;
    currenItemDatakey?: string;
    linkDataKey?: string;
    defaultValueOption?: string;
    placeholder?: string;
    currentItemDatakey?: string;
    targetTop?: boolean;
    topURLDataKey?: string;
    showSearch?: boolean;
    showSearchIcon?: boolean;
    searchIconPosition?: 'Right' | 'Left';
    searchIconType?: 'FontAwesome Icon' | 'Image';
    searchIcon?: string;
    searchCancelIcon?: string;
    searchImageUrl?: string;
    searchCancelImageUrl?: string;
    searchPlaceholder?: string;
    noOptionFoundText?: string;
    isDependent?: boolean;
    parentDropdownId?: string;
    dependentDataKey?: string;
    iconType?: '' | 'FontAwesome Icon' | 'Image';
    dropdownOpenIcon?: string;
    dropdownCloseIcon?: string;
    iconPosition?: 'Right' | 'Left';
    dropdownOpenImage?: string;
    dropdownCloseImage?: string;
    useCustomUrl?: boolean;
    customUrl?: string;
}

interface MultiDropDownProps {
    typographyClass?: string;
    dropdowns?: DropdownType[];
    transformData?: boolean;
    functionToTransformData?: string;
    useAnalytics?: boolean;
    dynamicEvents?: DynamicEvent[];
    useLowerCase?: boolean;
}
export interface WidgetProperties extends commonTypes.WidgetPropertiesBase, MultiDropDownProps {}

const MultiDropdown: React.FC<commonTypes.WidgetInstanceProps> = (props) => {
    const { isClient } = commonHooks.useAppContext();
    const { data } = props;
    const i18n = commonHooks.useLocalization();
    const tracking = commonAnalytics.useTracking();
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const { styles, dropdowns, useAnalytics, dynamicEvents, useLowerCase, ...rest } =
        widgetProperties || props;
    const theme = data?.themeSelected || props?.theme;
    const [dropdownList, setDropdownList] = useState<DropdownList[]>([]);
    const [showSearch, setShowSearch] = useState(false);
    const [searchValue, setSearchValue] = useState<any>({});
    const dropdownRef = useRef<HTMLDivElement>(null);
    const dropdownToggleRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const { pathname } = useLocation();

    let isRender: any = {
        dropdowns,
        dropdownList,
        data,
    };

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const _transformedData =
            rest.transformData &&
            commonUtils.transformDataWithGlobals(
                transformFunctionName,
                rest.functionToTransformData,
                {
                    data,
                    JsonQuery,
                    ...isRender,
                }
            );

        if (_transformedData) {
            isRender = { ...isRender, ..._transformedData };
        }
    } catch (e) {
        console.log(e);
    }

    const toggleDropdown = (dropdown: DropdownList, isOpen?: any) => {
        if (dropdown?.isDependent) {
            // check if parent list item is selected
            const isParentSelected = dropdownList
                ?.find((dl) => dl?.id === dl?.parentDropdownId)
                ?.listData?.find((ld: any) => ld.isSelected);
            if (isParentSelected) {
                setDropdownList((prevDropdowns) =>
                    prevDropdowns.map((dl) =>
                        dl.id === dropdown?.id ? { ...dl, isOpen: !dl?.isOpen } : dl
                    )
                );
            }
        } else {
            setDropdownList((prevDropdowns) =>
                prevDropdowns.map((dl) =>
                    dl.id === dropdown?.id
                        ? { ...dl, isOpen: isOpen !== undefined ? isOpen : !dl?.isOpen }
                        : dl
                )
            );
            setShowSearch(!showSearch);
        }
    };

    const toggleDropdownOnKeyDown = (
        event: React.KeyboardEvent<HTMLElement>,
        dropdown: DropdownList
    ) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.click();
            toggleDropdown(dropdown);
        }
    };

    // handle if click outside of drodown and toggle icon
    const handleClickOutside = (e: MouseEvent) => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(e.target as Node) &&
            dropdownToggleRef.current &&
            !dropdownToggleRef.current.contains(e.target as Node) &&
            searchInputRef.current &&
            !searchInputRef.current.contains(e.target as Node)
        ) {
            setDropdownList((prevDropdowns) =>
                prevDropdowns.map((dl) => ({ ...dl, isOpen: false }))
            );
        }
    };

    useEffect(() => {
        // event listener to check if click outside of component
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // remove the event listener
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        // This function will be called when the scroll event occurs
        const handleScroll = () => {
            // Close the dropdown by setting showDropdown to false
            setDropdownList((prevDropdowns) =>
                prevDropdowns.map((dl) => ({ ...dl, isOpen: false }))
            );
        };

        // Add an event listener to the window object for the scroll event
        window.addEventListener('scroll', handleScroll);

        // Clean up the event listener when the component is unmounted
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        if (dropdowns?.length) {
            const updatedDropdownList: DropdownList[] = dropdowns?.map((dropdown: DropdownType) => {
                let listData: any;
                // Get dropdown list from data
                if (
                    dropdown?.isDependent &&
                    (!dropdown?.dataKey || dropdown?.dataKey?.length === 0)
                ) {
                    const parentDD = dropdownList?.find(
                        (dl) => dl?.id === dropdown?.parentDropdownId
                    );

                    if (parentDD) {
                        listData = parentDD?.listData;
                    } else {
                        const _parentDD = dropdowns?.find(
                            (dl) => dl?.id === dropdown?.parentDropdownId
                        );
                        listData =
                            JsonQuery(_parentDD?.dataKey as string, { data: isRender.data })
                                ?.value || [];
                    }
                } else if (
                    dropdown?.isDependent &&
                    dropdown?.dataKey &&
                    dropdown?.dataKey?.length > 0
                ) {
                    const parentDD = dropdownList?.find(
                        (dl) => dl?.id === dropdown?.parentDropdownId
                    );
                    let parentListData;

                    if (parentDD) {
                        parentListData = parentDD?.listData;
                    } else {
                        const _parentDD = dropdowns?.find(
                            (dl) => dl?.id === dropdown?.parentDropdownId
                        );
                        parentListData =
                            JsonQuery(_parentDD?.dataKey as string, { data }).value || [];
                    }

                    listData =
                        JsonQuery(dropdown?.dataKey as string, {
                            data: { ...isRender.data, ...parentListData },
                        })?.value || [];
                } else {
                    listData =
                        JsonQuery(dropdown?.dataKey as string, { data: isRender.data })?.value ||
                        [];
                }
                if (dropdown?.currentItemDatakey) {
                    const listItem = listData.find(
                        (ld: any) => ld[dropdown?.currentItemDatakey || '']
                    );
                    listData = listData?.map((ld: any) =>
                        i18n.format(dropdown?.itemDataKey || '', {
                            ...ld,
                        }) ===
                        i18n.format(dropdown?.itemDataKey || '', {
                            ...listItem,
                        })
                            ? { ...ld, isSelected: true }
                            : ld
                    );
                }
                const currentSelectedValue = '';
                return { ...dropdown, isOpen: false, listData, currentSelectedValue };
            });

            setDropdownList(updatedDropdownList);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdowns, data]);

    // utility function for redirect
    const updateURL = (dropdown: DropdownList): void => {
        const selectedOption = dropdown?.listData?.find((ld: any) => ld?.isSelected);
        const id = i18n.format(dropdown?.linkDataKey || '', selectedOption);
        // check if page is within iframe
        if (dropdown.targetTop && isClient && window?.self !== window?.top) {
            const topURLDataKey = i18n.format(dropdown.topURLDataKey, data);
            const topUrlData = JsonQuery(`${topURLDataKey}`, { data })?.value;
            const url = topUrlData.find((d: any) => d?.entityId?.includes(id))?.url;
            // navigate to parent window
            if (window.top) window.top.location.href = url;
        } else if (dropdown?.useCustomUrl && dropdown?.customUrl) {
            // use custom url
            const URLDataKey = i18n.format(dropdown.customUrl, data);
            const UrlData = JsonQuery(`${URLDataKey}`, { data })?.value;
            const url = UrlData.find((d: any) => d?.entityId?.includes(id))?.url;
            window.location.href = url;
        } else {
            // navigate without iframe
            window.location.href = `${pathname}?profileId=${id}&locale=${data?.locale}`;
        }
    };

    const onClickItem = (dropdown: DropdownList, listDataSelectedIndex: number) => {
        const selectedValue = dropdown?.listData?.[listDataSelectedIndex] || '';
        setDropdownList((prevDropdowns) =>
            prevDropdowns?.map((dl) =>
                dl?.id === dropdown?.id
                    ? {
                          ...dl,
                          listData: dl?.listData?.map((ld: any, i: number) =>
                              i === listDataSelectedIndex
                                  ? { ...ld, isSelected: true }
                                  : { ...ld, isSelected: false }
                          ),
                          currentSelectedValue: selectedValue,
                      }
                    : dl
            )
        );
        const dependentDD = dropdownList?.find((dl) => dl?.parentDropdownId === dropdown?.id);
        if (dependentDD) {
            setDropdownList((prevDropdowns) =>
                prevDropdowns.map((dl) => {
                    if (dl?.id === dependentDD?.id) {
                        return { ...dl, isOpen: !dl.isOpen };
                    }
                    if (dl?.id === dropdown?.id) {
                        return { ...dl, isOpen: false };
                    }
                    return dl;
                })
            );
        } else {
            updateURL({
                ...dropdown,
                listData: dropdown?.listData?.map((ld: any, i: number) =>
                    i === listDataSelectedIndex
                        ? { ...ld, isSelected: true }
                        : { ...ld, isSelected: false }
                ),
            });
        }

        const selectedValueName = selectedValue[dropdown.id] || '';
        tracking({
            event: 'Selected Dropdown',
            data: data,
            dynamicEvents: dynamicEvents,
            useAnalytics: useAnalytics,
            selectedValueName: selectedValueName,
            useLowerCase: useLowerCase,
        });
    };
    const renderDefaultCurrentOption = (dropdown: DropdownList) => {
        const { listData, itemDataKey, currenItemDatakey } = dropdown;
        const selectedItem = listData?.find((ld: any) => ld?.isSelected);
        return (
            <span
                data-testid={'default-value'}
                dangerouslySetInnerHTML={{
                    __html: i18n.format(currenItemDatakey || itemDataKey || '', {
                        ...selectedItem,
                    }),
                }}
            />
        );
    };

    const renderFirstOption = (dropdown: DropdownList) => {
        const { listData, itemDataKey } = dropdown;

        return (
            Array.isArray(listData) &&
            listData?.length > 0 && (
                <span
                    dangerouslySetInnerHTML={{
                        __html: i18n.format(itemDataKey || '', {
                            ...listData[0],
                        }),
                    }}
                />
            )
        );
    };

    const renderIcon = (dropdown: DropdownList) => {
        return (
            <>
                {dropdown?.iconType === 'FontAwesome Icon' ? (
                    <i
                        className={`dropdown-toggle-icon fas ${
                            dropdown?.isOpen
                                ? dropdown?.dropdownCloseIcon
                                : dropdown?.dropdownOpenIcon
                        }`}
                        onClick={dropdown?.showSearch ? () => toggleDropdown(dropdown) : () => null}
                        onKeyDown={
                            dropdown?.showSearch
                                ? (e) => toggleDropdownOnKeyDown(e, dropdown)
                                : () => null
                        }
                        data-testid={'dropdown-toggle-icon'}
                    />
                ) : dropdown?.iconType === 'Image' ? (
                    <img
                        className={'dropdown-toggle-image'}
                        src={
                            dropdown?.isOpen
                                ? dropdown?.dropdownCloseImage
                                : dropdown?.dropdownOpenImage
                        }
                        alt={dropdown?.isOpen ? 'dropdown-toggle-open' : 'dropdown-toggle-close'}
                        onClick={dropdown?.showSearch ? () => toggleDropdown(dropdown) : () => null}
                        onKeyDown={
                            dropdown?.showSearch
                                ? (e) => toggleDropdownOnKeyDown(e, dropdown)
                                : () => null
                        }
                        data-testid={'dropdown-toggle-icon'}
                    />
                ) : null}
            </>
        );
    };

    const renderSearchIcon = (dropdown: DropdownList) => {
        return (
            <>
                {dropdown?.searchIconType === 'FontAwesome Icon' ? (
                    <i className={`dropdown-search-icon fas ${dropdown?.searchIcon}`} />
                ) : dropdown?.searchIconType === 'Image' ? (
                    <img
                        className={'dropdown-search-image'}
                        src={dropdown?.searchImageUrl}
                        alt={'dropdown-search'}
                    />
                ) : null}
            </>
        );
    };

    const renderCancelIcon = (dropdown: DropdownList) => {
        return (
            <>
                {dropdown?.searchIconType === 'FontAwesome Icon' ? (
                    <i
                        className={`dropdown-search-cancel-icon fas ${dropdown?.searchCancelIcon}`}
                        onClick={() =>
                            setSearchValue((prev: any) => ({ ...prev, [dropdown?.id]: '' }))
                        }
                    />
                ) : dropdown?.searchIconType === 'Image' ? (
                    <img
                        className={'dropdown-search-cancel-image'}
                        src={dropdown?.searchCancelImageUrl}
                        alt={'dropdown-search-cancel'}
                        onClick={() =>
                            setSearchValue((prev: any) => ({ ...prev, [dropdown?.id]: '' }))
                        }
                    />
                ) : null}
            </>
        );
    };

    const renderOptions = (dropdown: DropdownList) => {
        const { id, listData, itemDataKey } = dropdown;
        const filteredListData = listData.filter(
            (item: any) =>
                !searchValue?.[id] ||
                i18n
                    .format(itemDataKey || '', {
                        ...item,
                    })
                    ?.toLowerCase()
                    ?.includes(searchValue?.[id]?.toLowerCase())
        );

        return Array.isArray(filteredListData) && filteredListData.length > 0 ? (
            filteredListData?.map((item: any, index: number) => (
                <div
                    key={index}
                    tabIndex={0}
                    data-testid={`dropdown-option-${index}`}
                    className={'dropdown-item'}
                    onClick={() => onClickItem(dropdown, index)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            onClickItem(dropdown, index);
                        }
                    }}
                    dangerouslySetInnerHTML={{
                        __html: i18n.format(itemDataKey || '', {
                            ...item,
                        }),
                    }}
                />
            ))
        ) : (
            <span className="no-option-found">
                {i18n.format(dropdown?.noOptionFoundText, data)}
            </span>
        );
    };

    const renderPlaceholder = () => {
        if (dropdowns && dropdowns.length > 0 && dropdownList.length < 1) {
            return dropdowns.map((dropdown: DropdownType, i: number) => (
                <div className="dropdown-container" key={`${dropdown?.id}-${i}`}>
                    <div className="multi-dropdown-title">{i18n.format(dropdown?.label, data)}</div>
                    <Dropdown
                        id={dropdown?.id || 'dropdown-label'}
                        show={false}
                        aria-label={i18n.format(dropdown?.ariaLabel, data) || ''}
                    >
                        <Dropdown.Toggle
                            aria-label={`${i18n.format(dropdown?.label, isRender.data)}`}
                        >
                            {dropdown?.defaultValueOption === PLACEHOLDER ? (
                                <span className="placeholder" data-testid={'placeholder'}>
                                    {i18n.format(dropdown?.placeholder, data)}
                                </span>
                            ) : (
                                dropdown?.defaultValueOption === FIRSTOPTION &&
                                renderFirstOption(dropdown)
                            )}
                        </Dropdown.Toggle>
                    </Dropdown>
                </div>
            ));
        }
    };

    return (
        <div
            className={`multi-dropdown ${widgetProperties?.typographyClass ?? ''}`}
            css={css`
                ${theme ? multiDropdownStyle : ''}
                ${getTextSchemaProperties(widgetProperties, data?.colorSpace)}
                ${getStyleSchemaProperties(widgetProperties)}
                ${getLayoutSchemaProperties(widgetProperties)}
                ${styles ?? ''}
            `}
            data-testid={'multi-dropdown'}
        >
            {renderPlaceholder()}
            {dropdownList?.map((dropdown: DropdownList, i: number) => (
                <div className="dropdown-container" key={`${dropdown?.id}-${i}`}>
                    <div className="multi-dropdown-title">{i18n.format(dropdown?.label, data)}</div>
                    <Dropdown
                        id={dropdown?.id || 'dropdown-label'}
                        ref={dropdownRef}
                        show={dropdown?.isOpen}
                        aria-label={i18n.format(dropdown?.ariaLabel, isRender.data) || ''}
                        onToggle={(isOpen) =>
                            !dropdown?.showSearch ? toggleDropdown(dropdown, isOpen) : null
                        }
                    >
                        <Dropdown.Toggle
                            ref={dropdownToggleRef}
                            data-testid={'dropdown-toggle'}
                            aria-label={`${i18n.format(dropdown?.label, isRender.data)}` || ''}
                        >
                            {dropdown?.iconPosition === 'Left' && renderIcon(dropdown)}
                            {dropdown?.showSearch && showSearch ? (
                                <>
                                    {dropdown?.searchIconPosition === 'Left' &&
                                        (searchValue?.[dropdown?.id]?.length > 0
                                            ? renderCancelIcon(dropdown)
                                            : renderSearchIcon(dropdown))}
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        className="dropdown-search-input"
                                        placeholder={i18n.format(dropdown?.searchPlaceholder, data)}
                                        value={searchValue?.[dropdown?.id] || ''}
                                        onChange={(e) =>
                                            setSearchValue((prev: any) => ({
                                                ...prev,
                                                [dropdown?.id]: e.target.value,
                                            }))
                                        }
                                        data-testid={'search-input'}
                                    />
                                    {dropdown?.searchIconPosition === 'Right' &&
                                        (searchValue?.[dropdown?.id]?.length > 0
                                            ? renderCancelIcon(dropdown)
                                            : renderSearchIcon(dropdown))}
                                </>
                            ) : dropdown?.defaultValueOption === CURRENTOPTION ? (
                                dropdown?.listData?.find((ld: any) => ld.isSelected) ? (
                                    renderDefaultCurrentOption(dropdown)
                                ) : null
                            ) : dropdown?.defaultValueOption === PLACEHOLDER ? (
                                <span className="placeholder" data-testid={'placeholder'}>
                                    {i18n.format(dropdown?.placeholder, data)}
                                </span>
                            ) : (
                                dropdown?.defaultValueOption === FIRSTOPTION &&
                                renderFirstOption(dropdown)
                            )}
                            {dropdown?.iconPosition === 'Right' && renderIcon(dropdown)}
                        </Dropdown.Toggle>
                        <Dropdown.Menu data-testid={'dropdown-options'}>
                            {renderOptions(dropdown)}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            ))}
        </div>
    );
};

export default MultiDropdown;
